import React from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useForecastRefresh } from '@/contexts/ForecastRefreshContext';

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDurationShort(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  return `${min}m ${sec}s`;
}

export const ForecastRefreshBanner: React.FC = () => {
  const { refreshState } = useForecastRefresh();
  const { status, startTime, completionTime, triggeredBy, estimatedDurationMs } = refreshState;

  // Do not render anything if idle (banner never appears on first load)
  if (status === 'idle') return null;

  if (status === 'in-progress') {
    return (
      <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-800">
        <Loader2 className="h-4 w-4 text-amber-600 animate-spin flex-shrink-0" />
        <span>
          <strong>Forecast refresh in progress</strong>
          {startTime && <span className="font-normal text-amber-700"> · Started {formatTime(startTime)}</span>}
          {estimatedDurationMs > 0 && (
            <span className="font-normal text-amber-700"> · Estimated {formatDurationShort(estimatedDurationMs)}</span>
          )}
          {triggeredBy && <span className="font-normal text-amber-700"> · Triggered by {triggeredBy}</span>}
        </span>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className="w-full bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-2 text-sm text-green-800">
        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
        <span>
          <strong>Forecast refresh complete</strong>
          {completionTime && (
            <span className="font-normal text-green-700"> · Completed {formatTime(completionTime)}</span>
          )}
          <span className="font-normal text-green-700"> · New forecast now live</span>
        </span>
      </div>
    );
  }

  return null;
};
